import { get, post, put } from "../../../core/api/client";
import { API_ENDPOINTS } from "../../../core/api/endpoints";

const FALLBACK_PLAN_IDS = [1, 2, 3, 4, 5];
const FALLBACK_PLAN_NAMES = ["Basic", "Pro", "Team", "Enterprise", "Custom"];

const toNumber = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const toClientUpdatePayload = (client, updates = {}) => ({
  client_name: client.client_name,
  plan_id: client.plan_id,
  client_category: client.client_category,
  client_email: client.client_email,
  client_address: client.client_address,
  client_phone: client.client_phone,
  timezone: client.timezone,
  client_status: client.client_status,
  delivery_failure_notification: client.delivery_failure_notification,
  usage_alert_notification: client.usage_alert_notification,
  system_alert_notification: client.system_alert_notification,
  ...updates,
});

export const getBillingSubscriptionPlans = async (
  clientId,
  params = {},
) => {
  const { limit = 20, offset = 0 } = params;

  const [plansResponse, clientsResponse] = await Promise.all([
    get(API_ENDPOINTS.SUBSCRIPTION_PLANS.LIST(limit, offset)),
    get(API_ENDPOINTS.CLIENTS.LIST),
  ]);

  const clients = Array.isArray(clientsResponse?.clients)
    ? clientsResponse.clients
    : [];

  const targetClient =
    clients.find((item) => Number(item.id) === Number(clientId)) || null;

  const selectedPlanId =
    toNumber(targetClient?.plan_id, 0) ||
    toNumber(targetClient?.subscription_plan?.id, 0) ||
    null;

  const listFromResponse = Array.isArray(plansResponse?.subscription_plans)
    ? plansResponse.subscription_plans
    : Array.isArray(plansResponse?.plans)
      ? plansResponse.plans
      : [];

  let rawPlans = listFromResponse;

  if (!rawPlans.length) {
    const totalPlans = toNumber(plansResponse?.total, 0);
    const idsToFetch =
      totalPlans > 0
        ? Array.from({ length: Math.min(totalPlans, limit) }, (_, index) => index + 1)
        : FALLBACK_PLAN_IDS;

    const plansById = await Promise.all(
      idsToFetch.map(async (id) => {
        const response = await get(API_ENDPOINTS.SUBSCRIPTION_PLANS.GET_BY_ID(id));
        return response?.error ? null : response;
      }),
    );

    rawPlans = plansById.filter(Boolean);
  }

  if (!rawPlans.length) {
    const plansByName = await Promise.all(
      FALLBACK_PLAN_NAMES.map(async (name) => {
        const response = await get(API_ENDPOINTS.SUBSCRIPTION_PLANS.GET_BY_NAME(name));
        return response?.error ? null : response;
      }),
    );

    rawPlans = plansByName.filter(Boolean);
  }

  return {
    plans: rawPlans,
    currentClient: targetClient,
    currentPlanId: selectedPlanId,
    plansError: plansResponse?.error?.message || "",
    clientError: clientsResponse?.error?.message || "",
  };
};

export const requestCustomPlan = async ({
  name,
  email,
  phone,
  organization_name,
}) => {
  const payload = {
    name: String(name || "").trim(),
    email: String(email || "").trim(),
    phone: String(phone || "").trim(),
    organization_name: String(organization_name || "").trim(),
  };

  const response = await post(
    API_ENDPOINTS.SUBSCRIPTION_PLANS.CUSTOM_PLAN,
    payload,
  );

  if (response?.error) {
    return response;
  }

  return {
    message:
      response?.message ||
      "Your custom plan request has been sent. Our team will reach out shortly.",
  };
};

export const updateBillingClientPlan = async (client, planId) => {
  if (!client?.id) {
    return { error: { message: "Client id is required to update plan." } };
  }

  const payload = toClientUpdatePayload(client, {
    plan_id: Number(planId),
  });

  const response = await put(API_ENDPOINTS.CLIENTS.UPDATE(client.id), payload);

  if (response?.error) {
    return response;
  }

  return {
    ...response,
    client: {
      ...client,
      plan_id: Number(planId),
    },
  };
};
